import { EquipmentSection } from './equipment-section'
import { LaborSection } from './labor-section'
import { MaterialsSection } from './materials-section'
import { MiscSection } from './misc-section'
import { HaulDumpCalculator } from './haul-dump-calculator'
import type { PricingItem, LineItem, CreateLineItemPayload } from '@/types'
import type { LchTenantCost, LchRow } from '@/services/lch-service'

interface AsphaltGradingPanelProps {
  estimateId: string
  section: string
  serviceFilter?: string
  pricingItems: PricingItem[]
  lineItems: LineItem[]
  isPublicWorks?: boolean
  defaultFuelChargePercent?: number
  scopeData?: string
  initialCosts?: LchTenantCost[]
  initialLchData?: LchRow[]
  onAddItem: (payload: CreateLineItemPayload) => Promise<void>
  onUpdateItem: (itemId: string, data: Partial<CreateLineItemPayload> & { lineTotal?: number }) => Promise<void>
  onDeleteItem: (itemId: string) => Promise<void>
  onWriteupRowDisable?: (writeupRowId: string) => void
  onWriteupFieldUpdate?: (writeupRowId: string, fieldName: string, value: number) => void
  onLchDataChange?: (rows: import('@/services/lch-service').LchRow[]) => void
}

export function AsphaltGradingPanel({
  estimateId, section, serviceFilter, pricingItems, lineItems, isPublicWorks, defaultFuelChargePercent, scopeData, initialCosts, initialLchData, onAddItem, onUpdateItem, onDeleteItem, onWriteupRowDisable, onWriteupFieldUpdate, onLchDataChange,
}: AsphaltGradingPanelProps) {
  return (
    <div className="ec-panel">
      {section === 'asphalt' ? (
        <MaterialsSection
          lineItems={lineItems}
          pricingItems={pricingItems}
          sections={[
            { section: 'asphalt' },
            { section: 'asphalt-rnr', suffix: 'R&R' },
            { section: 'asphalt-overlay', suffix: 'Overlay' },
          ]}
          serviceFilter={serviceFilter}
          scopeData={scopeData}
          label="Asphalt — Construction Materials"
          onAddItem={onAddItem}
          onUpdateItem={onUpdateItem}
          onDeleteItem={onDeleteItem}
          onWriteupRowDisable={onWriteupRowDisable}
        />
      ) : (
        <MaterialsSection
          lineItems={lineItems}
          pricingItems={pricingItems}
          section={section}
          serviceFilter={serviceFilter}
          scopeData={scopeData}
          onAddItem={onAddItem}
          onUpdateItem={onUpdateItem}
          onDeleteItem={onDeleteItem}
          onWriteupRowDisable={onWriteupRowDisable}
        />
      )}
      {/* Haul & Dump Calculator — below Construction Materials */}
      {(section === 'asphalt' || section === 'concrete') && (
        <HaulDumpCalculator
          estimateId={estimateId}
          section={section}
          scopeData={scopeData}
          initialCosts={initialCosts}
          initialLchData={initialLchData}
          onWriteupRowDisable={onWriteupRowDisable}
          onWriteupFieldUpdate={onWriteupFieldUpdate}
          onLchDataChange={onLchDataChange}
        />
      )}
      {(section === 'asphalt' || section === 'concrete') && (
        <>
          <EquipmentSection
            lineItems={lineItems}
            pricingItems={pricingItems}
            section={`${section}-grading`}
            serviceFilter="Grading"
            label="Grading/Demo Equipment"
            defaultFuelChargePercent={defaultFuelChargePercent}
            onAddItem={onAddItem}
            onUpdateItem={onUpdateItem}
            onDeleteItem={onDeleteItem}
          />
          <LaborSection
            lineItems={lineItems}
            pricingItems={pricingItems}
            section={`${section}-grading`}
            serviceFilter="Grading"
            label="Grading/Demo Labor"
            isPublicWorks={isPublicWorks}
            onAddItem={onAddItem}
            onUpdateItem={onUpdateItem}
            onDeleteItem={onDeleteItem}
          />
        </>
      )}
      <EquipmentSection
        lineItems={lineItems}
        pricingItems={pricingItems}
        section={section}
        serviceFilter={serviceFilter}
        defaultFuelChargePercent={defaultFuelChargePercent}
        onAddItem={onAddItem}
        onUpdateItem={onUpdateItem}
        onDeleteItem={onDeleteItem}
      />
      <LaborSection
        lineItems={lineItems}
        pricingItems={pricingItems}
        section={section}
        serviceFilter={serviceFilter}
        isPublicWorks={isPublicWorks}
        onAddItem={onAddItem}
        onUpdateItem={onUpdateItem}
        onDeleteItem={onDeleteItem}
      />
      <MiscSection
        lineItems={lineItems}
        pricingItems={pricingItems}
        section={section}
        serviceFilter={serviceFilter}
        onAddItem={onAddItem}
        onUpdateItem={onUpdateItem}
        onDeleteItem={onDeleteItem}
      />
    </div>
  )
}
